import { useCallback } from "react";

export type CSVColumn<T> = {
  header: string;
  accessor: (item: T) => string | number | undefined | null;
};

export type UseCSVExportOptions = {
  baseFilename?: string;
};

function getUniqueFilename(baseFilename?: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return baseFilename ? `${baseFilename}-${timestamp}.csv` : `export-${timestamp}.csv`;
}

export function useCSVExport<T>(options?: UseCSVExportOptions) {
  const exportToCSV = useCallback(
    (data: T[], columns: CSVColumn<T>[]) => {
      if (data.length === 0) {
        throw new Error("No data to export");
      }

      const headers = columns.map(col => col.header);

      const csvRows = [
        headers.join(","),
        ...data.map(item => {
          const row = columns.map(col => {
            const value = col.accessor(item);
            const stringValue = value?.toString() ?? "";
            // Escape quotes and prevent formula injection
            let escaped = stringValue.replace(/"/g, '""');
            // Prefix with single quote if starts with formula characters
            if (/^[=+\-@\t\r]/.test(escaped)) escaped = "'" + escaped;
            return `"${escaped}"`;
          });
          return row.join(",");
        }),
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      const filename = getUniqueFilename(options?.baseFilename);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      return data.length;
    },
    [options?.baseFilename]
  );

  return { exportToCSV };
}

export function csvColumn<T>(
  header: string,
  accessor: (item: T) => string | number | undefined | null
): CSVColumn<T> {
  return { header, accessor };
}
